import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Zap, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TariffSettingsDialog({ open, currentTariff, onClose, onSuccess }) {
  const [tariff, setTariff] = useState(currentTariff);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tariff || parseFloat(tariff) <= 0) {
      toast.error('Tarif harus lebih dari 0');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/energy/tariff`,
        { tariff: parseFloat(tariff) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onSuccess();
    } catch (error) {
      console.error('Error setting tariff:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan tarif');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (!open) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6" />
            <h2 className="text-xl font-bold">Pengaturan Tarif Listrik</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              Tarif listrik digunakan untuk menghitung biaya operasional dari konsumsi energy.
              Sesuaikan dengan tarif PLN Anda.
            </p>
          </div>

          {/* Current Tariff */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Tarif Saat Ini:</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(currentTariff)}/kWh</p>
          </div>

          {/* New Tariff Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tarif Baru (Rp per kWh) *
            </label>
            <input
              type="number"
              step="1"
              min="0"
              value={tariff}
              onChange={(e) => setTariff(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-lg font-semibold"
              placeholder="1500"
              required
            />
            <p className="mt-2 text-sm text-gray-500">
              Preview: {formatCurrency(parseFloat(tariff) || 0)}/kWh
            </p>
          </div>

          {/* Common Tariffs */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Tarif PLN Umum:</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTariff(1352)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-left"
              >
                <div className="font-medium">R-1 / 900 VA</div>
                <div className="text-xs text-gray-500">Rp 1.352/kWh</div>
              </button>
              <button
                type="button"
                onClick={() => setTariff(1444)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-left"
              >
                <div className="font-medium">R-1 / 1300 VA</div>
                <div className="text-xs text-gray-500">Rp 1.444/kWh</div>
              </button>
              <button
                type="button"
                onClick={() => setTariff(1699)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-left"
              >
                <div className="font-medium">R-1 / 2200 VA</div>
                <div className="text-xs text-gray-500">Rp 1.699/kWh</div>
              </button>
              <button
                type="button"
                onClick={() => setTariff(1500)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-left"
              >
                <div className="font-medium">Custom</div>
                <div className="text-xs text-gray-500">Rp 1.500/kWh</div>
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {submitting ? 'Menyimpan...' : 'Simpan Tarif'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
